import { useCallback, useEffect, useState } from 'react'
import { ApiHatasi, api, type DanisanOzet } from '../api'

function yas(dogum: string): string {
  const d = new Date(dogum)
  if (Number.isNaN(d.getTime())) return '—'
  const bugun = new Date()
  let y = bugun.getFullYear() - d.getFullYear()
  const ayFarki = bugun.getMonth() - d.getMonth()
  if (ayFarki < 0 || (ayFarki === 0 && bugun.getDate() < d.getDate())) y--
  return `${y}`
}

export function Danisanlar({
  onAc,
  yazabilir,
}: {
  onAc: (id: string) => void
  yazabilir: boolean
}) {
  const [liste, setListe] = useState<DanisanOzet[] | null>(null)
  const [ara, setAra] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [formAcik, setFormAcik] = useState(false)

  const yukle = useCallback(async (q?: string) => {
    setHata(null)
    try {
      const r = await api.danisanlar(q)
      setListe(r.danisanlar)
    } catch {
      setHata('Liste alınamadı. Bağlantıyı kontrol edip tekrar deneyin.')
      setListe([])
    }
  }, [])

  useEffect(() => {
    void yukle()
  }, [yukle])

  return (
    <>
      <h1>Danışanlar</h1>
      <p className="alt">Dosya açmak, ölçüm başlatmak ve raporu görmek için bir danışan seçin.</p>

      {hata && <div className="uyari">{hata}</div>}

      <form
        className="dugmeler"
        style={{ marginBottom: 14 }}
        onSubmit={(e) => {
          e.preventDefault()
          void yukle(ara.trim() || undefined)
        }}
      >
        <input
          placeholder="Ad, soyad veya dosya no"
          value={ara}
          onChange={(e) => setAra(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <button type="submit">Ara</button>
        {ara && (
          <button
            type="button"
            onClick={() => {
              setAra('')
              void yukle()
            }}
          >
            Temizle
          </button>
        )}
        {yazabilir && (
          <button
            type="button"
            className="birincil"
            style={{ marginLeft: 'auto' }}
            onClick={() => setFormAcik((v) => !v)}
          >
            {formAcik ? 'Vazgeç' : 'Yeni danışan'}
          </button>
        )}
      </form>

      {formAcik && (
        <YeniDanisanFormu
          onIptal={() => setFormAcik(false)}
          onKayit={(id) => {
            setFormAcik(false)
            onAc(id)
          }}
        />
      )}

      {liste === null ? (
        <p className="alt">Yükleniyor…</p>
      ) : liste.length === 0 ? (
        <div className="bilgi">
          {ara
            ? 'Bu aramaya uyan danışan yok.'
            : 'Henüz danışan yok. Ölçüm başlatabilmek için önce dosya açmanız gerekiyor.'}
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Dosya no</th>
              <th>Ad soyad</th>
              <th>Yaş</th>
              <th>Durum</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((d) => (
              <tr key={d.id} className="tiklanir" onClick={() => onAc(d.id)}>
                <td>{d.dosyaNo}</td>
                <td>
                  {d.ad} {d.soyad}
                </td>
                <td>{yas(d.dogumTarihi)}</td>
                <td>
                  <span className={'rozet' + (d.durum === 'active' ? ' ok' : '')}>
                    {d.durum === 'active' ? 'Aktif' : d.durum}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  )
}

function YeniDanisanFormu({
  onIptal,
  onKayit,
}: {
  onIptal: () => void
  onKayit: (id: string) => void
}) {
  const [ad, setAd] = useState('')
  const [soyad, setSoyad] = useState('')
  const [dogumTarihi, setDogum] = useState('')
  const [dosyaNo, setDosyaNo] = useState('')
  const [okul, setOkul] = useState('')
  const [sinif, setSinif] = useState('')
  const [telefon, setTelefon] = useState('')
  const [yonlendiren, setYonlendiren] = useState('')
  const [onDegerlendirme, setOn] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [bekliyor, setBekliyor] = useState(false)

  const gonder = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setBekliyor(true)
    try {
      const r = await api.danisanEkle({
        dosyaNo: dosyaNo.trim(),
        ad: ad.trim(),
        soyad: soyad.trim(),
        dogumTarihi,
        okul: okul.trim() || undefined,
        sinif: sinif.trim() || undefined,
        telefon: telefon.trim() || undefined,
        yonlendiren: yonlendiren.trim() || undefined,
        onDegerlendirme: onDegerlendirme.trim() || undefined,
      })
      onKayit(r.danisan.id)
    } catch (err) {
      if (err instanceof ApiHatasi && err.kod === 'dosya_no_kullanimda') {
        setHata('Bu dosya numarası merkezinizde başka bir danışanda kullanılıyor.')
      } else if (err instanceof ApiHatasi && err.durum === 403) {
        setHata('Dosya açma yetkiniz yok.')
      } else {
        setHata('Kaydedilemedi. Alanları kontrol edip tekrar deneyin.')
      }
    } finally {
      setBekliyor(false)
    }
  }

  return (
    <form className="kart" onSubmit={gonder}>
      <h2 style={{ marginTop: 0 }}>Yeni dosya</h2>
      {hata && <div className="uyari">{hata}</div>}
      <div className="satir">
        <label>
          Ad
          <input value={ad} onChange={(e) => setAd(e.target.value)} required maxLength={80} />
        </label>
        <label>
          Soyad
          <input value={soyad} onChange={(e) => setSoyad(e.target.value)} required maxLength={80} />
        </label>
        <label>
          Doğum tarihi
          <input
            type="date"
            value={dogumTarihi}
            onChange={(e) => setDogum(e.target.value)}
            required
          />
        </label>
        <label>
          Dosya no
          <input
            value={dosyaNo}
            onChange={(e) => setDosyaNo(e.target.value)}
            required
            maxLength={40}
            placeholder="örn. 2026-014"
          />
        </label>
        <label>
          Okul
          <input value={okul} onChange={(e) => setOkul(e.target.value)} maxLength={120} />
        </label>
        <label>
          Sınıf
          <input value={sinif} onChange={(e) => setSinif(e.target.value)} maxLength={40} />
        </label>
        <label>
          Telefon
          <input value={telefon} onChange={(e) => setTelefon(e.target.value)} maxLength={40} />
        </label>
        <label>
          Yönlendiren
          <input
            value={yonlendiren}
            onChange={(e) => setYonlendiren(e.target.value)}
            maxLength={120}
          />
        </label>
      </div>
      <label>
        Ön değerlendirme
        <textarea
          rows={3}
          value={onDegerlendirme}
          onChange={(e) => setOn(e.target.value)}
          maxLength={4000}
          placeholder="Başvuru nedeni, aileden gelen bilgi"
        />
      </label>
      <div className="dugmeler">
        <button className="birincil" type="submit" disabled={bekliyor}>
          {bekliyor ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
        <button type="button" onClick={onIptal}>
          Vazgeç
        </button>
      </div>
    </form>
  )
}
